<template>
    <VJoyIconButton
        v-show="isDisplayMobile && !isMobileOpen"
        icon="menu"
        variant="secondary"
        size="small"
        class="organization-navbar__open"
        data-testid="organization-navbar-open"
        :label="$t('navbar.header.menu.expand')"
        @click="openMobileNavbar"
    />
    <Transition name="organization-navbar-overlay">
        <div v-if="isDisplayMobile && isMobileOpen" class="organization-navbar__overlay" @click="closeMobileNavbar" />
    </Transition>
    <aside class="organization-navbar" :class="{'organization-navbar--mobile-open': isMobileOpen}">
        <WhiteLabel
            v-if="organizationDetails!.organizationName"
            :company-name="organizationDetails!.organizationName"
            :company-logo="organizationDetails!.logoUrl"
            :is-reduced="false"
        />
        <VJoyWrapper v-else column-gap="2" align="center" no-margin class="organization-navbar__organization-avatar">
            <VJoyCompanyAvatar :company-name="organizationDetails!.organizationName" />
            <VJoyText preset="primary-450" class="organization-navbar__organization-name" data-testid="organization-navbar-organization-name">
                {{ organizationDetails!.organizationName }}
            </VJoyText>
        </VJoyWrapper>

        <VJoyWrapper no-margin direction="row" row-gap="2" class="organization-navbar__items">
            <template v-for="link in filteredNavigationLinks" :key="link.id">
                <template v-if="link.children?.length">
                    <VJoyCollapse>
                        <VJoyCollapseItem
                            :header="link.label ?? $t(`client.organization.settings.nav.tabs.${link.id as keyof typeof ROUTES}`)"
                            :open="isParentExpanded(link)"
                        >
                            <div class="organization-navbar__subitems">
                                <NuxtLink
                                    v-for="child in link.children"
                                    :key="child.id"
                                    :class="{active: child.active}"
                                    :to="child.isComingSoon || child.active ? {} : child.to"
                                    :external="child.external"
                                    tag="button"
                                    class="organization-navbar__item organization-navbar__item--child"
                                    :disabled="child.isComingSoon ? true : undefined"
                                    :data-testid="navItemTestId(child)"
                                    :aria-current="child.active ? 'page' : undefined"
                                >
                                    <VJoyWrapper no-margin column-gap="2">
                                        <span class="organization-navbar__label-wrapper">
                                            <VJoyText :preset="`${child.active ? 'primary-350' : 'primary-300'}`" color="white">
                                                {{ child.label ?? $t(`client.organization.settings.nav.tabs.${child.id as keyof typeof ROUTES}`) }}
                                            </VJoyText>
                                            <VJoyDot
                                                v-if="link.notificationCount ?? 0 > 0"
                                                variant="orange"
                                                :number="link.notificationCount"
                                                class="joy-ml-2"
                                            />
                                        </span>
                                        <VJoyTag
                                            v-if="child.isComingSoon"
                                            :bulletpoint="false"
                                            size="xsmall"
                                            :label="$t('client.organization.settings.nav.coming-soon')"
                                            variant="inactive"
                                        />
                                    </VJoyWrapper>
                                </NuxtLink>
                            </div>
                        </VJoyCollapseItem>
                    </VJoyCollapse>
                </template>
                <template v-else>
                    <NuxtLink
                        :class="{active: link.active}"
                        :to="link.isComingSoon || link.active ? {} : link.to"
                        :external="link.external"
                        tag="button"
                        class="organization-navbar__item"
                        :disabled="link.isComingSoon ? true : undefined"
                        :data-testid="navItemTestId(link)"
                        :aria-current="link.active ? 'page' : undefined"
                    >
                        <VJoyWrapper no-margin column-gap="2">
                            <span class="organization-navbar__label-wrapper">
                                <VJoyText :preset="`${link.active ? 'primary-350' : 'primary-300'}`" color="white">
                                    {{ link.label ?? $t(`client.organization.settings.nav.tabs.${link.id as keyof typeof ROUTES}`) }}
                                </VJoyText>
                                <VJoyDot v-if="link.notificationCount ?? 0 > 0" variant="orange" :number="link.notificationCount" class="joy-ml-2" />
                            </span>
                            <VJoyTag
                                v-if="link.isComingSoon"
                                :bulletpoint="false"
                                size="xsmall"
                                :label="$t('client.organization.settings.nav.coming-soon')"
                                variant="inactive"
                            />
                        </VJoyWrapper>
                    </NuxtLink>
                </template>
            </template>
        </VJoyWrapper>
        <div class="organization-navbar__bottom">
            <VJoyWrapper
                no-margin
                column-gap="4"
                class="organization-navbar__item organization-navbar__item--back"
                align="center"
                @click="navigateTo('/', {external: true})"
            >
                <VJoyIcon color="neutral-50" name="arrow-left" size="xsmall" />
                <VJoyText preset="primary-400">
                    {{ $t('client.organization.settings.nav.back') }}
                </VJoyText>
            </VJoyWrapper>
            <VJoyWrapper v-if="isDisplayMobile" no-margin column-gap="2" align="flex-end" justify="flex-end">
                <VJoyIconButton
                    icon="cross"
                    variant="ghost"
                    size="medium"
                    class="organization-navbar__close"
                    data-testid="organization-navbar-close"
                    :label="$t('navbar.header.menu.reduce')"
                    @click="closeMobileNavbar"
                />
            </VJoyWrapper>
        </div>
    </aside>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {WhiteLabel} from '@navbar/logged/common/ui';
    import {storeToRefs} from 'pinia';
    import {useRoute} from 'vue-router';
    import {navigateTo} from '#app';
    import {useAuth} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {
        VJoyCollapse,
        VJoyCollapseItem,
        VJoyCompanyAvatar,
        VJoyDot,
        VJoyIcon,
        VJoyIconButton,
        VJoyTag,
        VJoyText,
        VJoyWrapper,
    } from '@maltjoy/core-vue';
    import {useLegalEntityEditionRights} from '~/composables/organization-settings/useLegalEntityEditionRights';
    import {useOrganizationDetailsStore} from '~/features/organization-settings/organization-details.store';
    import type {NavigationLink} from '~/types/navigation';
    import {useNavigation} from '@/composables/organization-settings/useNavigation';
    import {useOrganizationSettingsMobileNavbar} from '@/composables/organization-settings/useOrganizationSettingsMobileNavbar';

    const integrationGatewayEnabled = useFeatureFlag('integration-gateway');
    const isTeamsPageEnabled = useFeatureFlag('admin-console-teams-page');
    const authStore = useAuth();
    const {user} = storeToRefs(authStore);
    const isPlatformAdmin = computed(() => user.value?.platformAdmin || false);

    const {organizationDetails, organizationId} = storeToRefs(useOrganizationDetailsStore());
    const route = useRoute();
    const {hasLegalEntityEditionRights} = useLegalEntityEditionRights();

    const {navigationLinks, ROUTES} = useNavigation({
        integrationGatewayEnabled: integrationGatewayEnabled.value,
        isPlatformAdmin: isPlatformAdmin.value,
        organizationId,
        isTeamsPageEnabled: isTeamsPageEnabled.value,
        hasLegalEntityEditionRights: !!hasLegalEntityEditionRights(organizationId.value!),
    });

    const filteredNavigationLinks = computed(() =>
        navigationLinks.value.filter((link) => link.constraint !== false).map((link) => withActiveState(link, route.path)),
    );

    const {isDisplayMobile, isMobileOpen, openMobileNavbar, closeMobileNavbar} = useOrganizationSettingsMobileNavbar();

    function withActiveState(link: NavigationLink, path: string): NavigationLink {
        const children = link.children?.map((child) => withActiveState(child, path));

        return {
            ...link,
            children,
            active: children?.length ? false : link.activePattern.test(path),
        };
    }

    function isParentExpanded(link: NavigationLink) {
        return link.children?.some((child) => child.activePattern.test(route.path)) ?? false;
    }

    function navItemTestId(link: NavigationLink) {
        return link.dataTestId ?? `organization-navbar-item-${link.id.toLowerCase()}`;
    }
</script>
<style scoped lang="scss">
    @use '@malt/joy-entrypoint' as joy;
    $inner-padding-lg: var(--joy-core-spacing-4);
    $inner-padding-sm: var(--joy-core-spacing-2);
    $outer-padding: var(--joy-core-spacing-2) var(--joy-core-spacing-4);
    $navbar-width: 290px;

    .organization-navbar {
        width: $navbar-width;
        position: fixed;
        z-index: 10;
        padding: var(--joy-core-spacing-2) var(--joy-core-spacing-4);
        top: 0;
        left: 0;
        box-shadow: var(--joy-core-elevation-2);
        bottom: 0;
        background-color: var(--joy-color-neutral-0);
        display: flex;
        flex-direction: column;

        @include joy.screen_sm {
            width: min($navbar-width, 90vw);
            z-index: var(--joy-core-z-index-dialog);
            transform: translateX(-100%);
            transition: transform var(--joy-transition-duration-default) ease;

            &--mobile-open {
                transform: translateX(0);
            }
        }

        &__open {
            background-color: var(--joy-color-neutral-0);
            position: fixed;
            top: var(--joy-core-spacing-3);
            left: var(--joy-core-spacing-3);
            z-index: var(--joy-core-z-index-dialog);
            box-shadow: var(--joy-core-elevation-2);
        }

        &__overlay {
            position: fixed;
            inset: 0;
            background-color: var(--joy-color-overlay);
            z-index: var(--joy-core-z-index-backdrop);
        }

        &__bottom {
            margin-top: auto;
        }

        a,
        p {
            text-decoration: none;
            @include joy.getFontBySize('primary-300');
        }

        &__item {
            padding: $inner-padding-lg;
            border-radius: var(--joy-core-radius-2);
            display: flex;
            width: 100%;

            &:not([disabled]):hover,
            &.active {
                font-weight: var(--joy-font-weight-bold);
                background-color: var(--joy-color-secondary-10);
            }

            &--back {
                background-color: var(--joy-color-neutral-0);
                cursor: pointer;
            }

            &--child {
                padding-left: var(--joy-core-spacing-8);
            }
        }

        &__items {
            margin-top: var(--joy-core-spacing-4);
            overflow-y: auto;
        }

        &__subitems {
            margin-left: var(--joy-core-spacing-2);
            display: flex;
            flex-direction: column;
            row-gap: var(--joy-core-spacing-1);
        }

        :deep(.joy-collapse) {
            width: 100%;
        }

        :deep(.joy-collapse-item) {
            margin: 0;
        }

        :deep(.joy-collapse-item > div:first-child) {
            padding: $inner-padding-lg;
            border-radius: var(--joy-core-radius-2);
            display: flex;
            width: 100%;
            cursor: pointer;
            @include joy.getFontBySize('primary-300');
            color: var(--joy-color-neutral-50);
        }

        :deep(.joy-collapse-item > div:first-child:hover) {
            background-color: var(--joy-color-secondary-10);
        }

        &__label-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
        }

        &__organization-name {
            max-width: 180px;
        }
    }

    .organization-navbar-overlay-enter-active,
    .organization-navbar-overlay-leave-active {
        transition: opacity var(--joy-transition-duration-default) ease;
    }

    .organization-navbar-overlay-enter-from,
    .organization-navbar-overlay-leave-to {
        opacity: 0;
    }
</style>
